import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { oxygen } from '../services/api';
import Swal from 'sweetalert2';
import { 
  FaFileInvoice, 
  FaDownload, 
  FaEye, 
  FaSearch, 
  FaFilter, 
  FaCalendarAlt,
  FaMoneyBillWave,
  FaUser,
  FaBuilding,
  FaChevronLeft,
  FaChevronRight,
  FaTimes,
  FaSpinner
} from 'react-icons/fa';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const OxygenInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 20,
    total: 0
  });

  // Filters
  const [filters, setFilters] = useState({
    search: '',
    dateFrom: null,
    dateTo: null,
    contactId: '',
    invoiceNumber: '',
    invoiceId: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(null);

  // Fetch invoices
  const fetchInvoices = async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        page: page.toString(),
        per_page: pagination.per_page.toString()
      };

      if (filters.dateFrom) {
        params.date_from = filters.dateFrom.toISOString().split('T')[0];
      }
      if (filters.dateTo) {
        params.date_to = filters.dateTo.toISOString().split('T')[0];
      }
      if (filters.contactId.trim()) {
        params.contact_id = filters.contactId.trim();
      }
      if (filters.invoiceNumber.trim()) {
        params.number = filters.invoiceNumber.trim();
      }
      if (filters.invoiceId.trim()) {
        params.id = filters.invoiceId.trim();
      }

      const response = await oxygen.getInvoices(params);
      setInvoices(response.data.data || []);
      setPagination(response.data.meta || pagination);
      setError(null);
    } catch (err) {
      console.error('Error fetching invoices:', err);
      setError('Failed to fetch invoices');
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch invoices from Oxygen'
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch specific invoice details
  const fetchInvoiceDetails = async (invoiceId) => {
    try {
      const response = await oxygen.getInvoiceById(invoiceId);
      setSelectedInvoice(response.data);
      setShowInvoiceModal(true);
    } catch (err) {
      console.error('Error fetching invoice details:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch invoice details'
      });
    }
  };

  // Open invoice PDF via iview_url
  const openInvoicePdf = (invoice) => {
    try {
      setDownloadingPdf(invoice.id);
      
      // Check if iview_url exists in the invoice data
      if (invoice.iview_url) {
        // Open the iview_url in a new tab
        window.open(invoice.iview_url, '_blank');
      } else {
        // Fallback: fetch invoice details to get iview_url
        fetchInvoiceViewUrl(invoice.id);
      }
    } catch (err) {
      console.error('Error opening PDF:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to open invoice PDF'
      });
    } finally {
      setDownloadingPdf(null);
    }
  };

  // Fetch invoice view URL if not available in invoice data
  const fetchInvoiceViewUrl = async (invoiceId) => {
    try {
      const response = await oxygen.getInvoiceViewUrl(invoiceId);
      if (response.data && response.data.viewUrl) {
        window.open(response.data.viewUrl, '_blank');
      } else {
        throw new Error('View URL not found');
      }
    } catch (err) {
      console.error('Error fetching invoice view URL:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to get invoice view URL'
      });
    }
  };

  // Apply filters
  const applyFilters = () => {
    fetchInvoices(1);
    setShowFilters(false);
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      search: '',
      dateFrom: null,
      dateTo: null,
      contactId: '',
      invoiceNumber: '',
      invoiceId: ''
    });
    fetchInvoices(1);
  };
  // Parse numeric value from string or return 0
  const parseNumeric = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const parsed = typeof value === 'string' ? parseFloat(value) : value;
    return isNaN(parsed) ? 0 : parsed;
  };

  // Format currency
  const formatCurrency = (amount) => {
    const numericAmount = parseNumeric(amount);
    return new Intl.NumberFormat('el-GR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format(numericAmount);
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('el-GR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  // Get status color
  const getStatusColor = (invoice) => {
    const amountPending = parseNumeric(invoice.amount_pending);
    const amountPaid = parseNumeric(invoice.amount_paid);
    const totalAmount = parseNumeric(invoice.total_amount);
    
    if (amountPending > 0) return 'text-yellow-600 bg-yellow-100';
    if (amountPaid >= totalAmount) return 'text-green-600 bg-green-100';
    return 'text-red-600 bg-red-100';
  };

  // Get status text
  const getStatusText = (invoice) => {
    const amountPending = parseNumeric(invoice.amount_pending);
    const amountPaid = parseNumeric(invoice.amount_paid);
    const totalAmount = parseNumeric(invoice.total_amount);
    
    if (amountPending > 0) return 'Partially Paid';
    if (amountPaid >= totalAmount) return 'Paid';
    return 'Unpaid';
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  if (loading && invoices.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"
        />
        <span className="ml-3 text-gray-600">Loading invoices...</span>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-blue-500 rounded-xl">
              <FaFileInvoice className="text-white text-xl" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Invoices/Receipts</h1>
              <p className="text-gray-600">Manage and view Generated Invoices/Receipts</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <FaFilter className="mr-2" />
              Filters
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => fetchInvoices(pagination.current_page)}
              disabled={loading}
              className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50"
            >
              <FaSearch className="mr-2" />
              Refresh
            </motion.button>
          </div>
        </div>
      </motion.div>

      {/* Filters Panel */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-6 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Filter Invoices</h3>
                <button
                  onClick={() => setShowFilters(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FaTimes />
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date From
                  </label>
                  <DatePicker
                    selected={filters.dateFrom}
                    onChange={(date) => setFilters({ ...filters, dateFrom: date })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholderText="Select start date"
                    dateFormat="yyyy-MM-dd"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date To
                  </label>
                  <DatePicker
                    selected={filters.dateTo}
                    onChange={(date) => setFilters({ ...filters, dateTo: date })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholderText="Select end date"
                    dateFormat="yyyy-MM-dd"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contact ID
                  </label>
                  <input
                    type="text"
                    value={filters.contactId}
                    onChange={(e) => setFilters({ ...filters, contactId: e.target.value })}
                    placeholder="Enter contact ID"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Invoice Number
                  </label>
                  <input
                    type="text"
                    value={filters.invoiceNumber}
                    onChange={(e) => setFilters({ ...filters, invoiceNumber: e.target.value })}
                    placeholder="Enter invoice number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Invoice ID
                  </label>
                  <input
                    type="text"
                    value={filters.invoiceId}
                    onChange={(e) => setFilters({ ...filters, invoiceId: e.target.value })}
                    placeholder="Enter invoice ID"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Clear Filters
                </button>
                <button
                  onClick={applyFilters}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Stats Cards */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8"
      >
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Invoices</p>
              <p className="text-2xl font-bold text-gray-900">{pagination.total}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <FaFileInvoice className="text-blue-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Amount</p>              <p className="text-2xl font-bold text-green-600">
                {formatCurrency(invoices.reduce((sum, inv) => sum + parseNumeric(inv.total_amount), 0))}
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <FaMoneyBillWave className="text-green-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Paid Amount</p>              <p className="text-2xl font-bold text-blue-600">
                {formatCurrency(invoices.reduce((sum, inv) => sum + parseNumeric(inv.amount_paid), 0))}
              </p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <FaMoneyBillWave className="text-blue-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pending Amount</p>              <p className="text-2xl font-bold text-yellow-600">
                {formatCurrency(invoices.reduce((sum, inv) => sum + parseNumeric(inv.amount_pending), 0))}
              </p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-lg">
              <FaMoneyBillWave className="text-yellow-600" />
            </div>
          </div>
        </div>
      </motion.div>

      {/* Invoices Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
        >
          <div className="overflow-x-auto">
            <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Document
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            VAT (ΑΦΜ)
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Amount
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {invoices.map((invoice, index) => (
              <motion.tr
            key={invoice.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.05 }}
            className="hover:bg-gray-50 transition-colors"
              >
            <td className="px-6 py-4 whitespace-nowrap">
              <div>
                <div className="text-sm font-medium text-gray-900">
              #{invoice.number}
                </div>
                <div className="text-sm text-gray-500">
              {invoice.document_type === 'rs' ? 'Receipt' : 'Invoice'}
                </div>
              </div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
              <div>
                <div className="text-sm text-black">
              {invoice.contact_vat || 'N/A'}
                </div>
              </div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
              {formatDate(invoice.issue_date)}
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
              <div>
                <div className="text-sm font-medium text-gray-900">
              {formatCurrency(invoice.total_amount)}
                </div>
                <div className="text-sm text-gray-500">
              Paid: {formatCurrency(invoice.amount_paid)}
                </div>
              </div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(invoice)}`}>
                {getStatusText(invoice)}
              </span>
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
              <div className="flex space-x-2">
                <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => fetchInvoiceDetails(invoice.id)}
              className="text-blue-600 hover:text-blue-900"
              title="View Details"
                >
              <FaEye />
                </motion.button>
                <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => openInvoicePdf(invoice)}
              disabled={downloadingPdf === invoice.id}
              className="text-green-600 hover:text-green-900 disabled:opacity-50"
              title="Open PDF"
                >
              {downloadingPdf === invoice.id ? <FaSpinner className="animate-spin" /> : <FaDownload />}
                </motion.button>
              </div>
            </td>
              </motion.tr>
            ))}
          </tbody>
            </table>
          </div>

          {/* Pagination */}
        {pagination.last_page > 1 && (
          <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to{' '}
                {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of{' '}
                {pagination.total} results
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => fetchInvoices(pagination.current_page - 1)}
                  disabled={pagination.current_page === 1 || loading}
                  className="px-3 py-1 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FaChevronLeft />
                </button>
                <span className="px-3 py-1 bg-blue-500 text-white rounded-md text-sm font-medium">
                  {pagination.current_page}
                </span>
                <button
                  onClick={() => fetchInvoices(pagination.current_page + 1)}
                  disabled={pagination.current_page === pagination.last_page || loading}
                  className="px-3 py-1 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FaChevronRight />
                </button>
              </div>
            </div>
          </div>
        )}
      </motion.div>

      {/* Invoice Details Modal */}
      <AnimatePresence>
        {showInvoiceModal && selectedInvoice && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowInvoiceModal(false);
                setSelectedInvoice(null);
              }
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Invoice Details #{selectedInvoice.number}
                  </h2>
                  <button
                    onClick={() => {
                      setShowInvoiceModal(false);
                      setSelectedInvoice(null);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <FaTimes className="text-xl" />
                  </button>
                </div>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                            <div className="space-y-4">
                            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                              <FaFileInvoice className="mr-2 text-blue-500" />
                              Invoice Information
                            </h3>
                            <div className="space-y-2">
                              <div>
                              <span className="text-sm font-medium text-gray-500">Invoice ID:</span>
                              <span className="ml-2 text-sm text-gray-900">{selectedInvoice.id}</span>
                              </div>
                              <div>
                              <span className="text-sm font-medium text-gray-500">Number:</span>
                              <span className="ml-2 text-sm text-gray-900">#{selectedInvoice.number}</span>
                              </div>
                              <div>
                              <span className="text-sm font-medium text-gray-500">Issue Date:</span>
                              <span className="ml-2 text-sm text-gray-900">{formatDate(selectedInvoice.issue_date)}</span>
                              </div>
                              <div>
                              <span className="text-sm font-medium text-gray-500">Document Type:</span>
                              <span className="ml-2 text-sm text-gray-900">{selectedInvoice.document_type === 'rs' ? 'Receipt' : 'Invoice'}</span>
                              </div>
                              <div>
                              <span className="text-sm font-medium text-gray-500">Description:</span>
                              <span className="ml-2 text-sm text-gray-900">{selectedInvoice.description || 'N/A'}</span>
                              </div>
                            </div>
                            </div>

                            {/* Contact Info */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <FaUser className="mr-2 text-green-500" />
                      Contact Information
                    </h3>
                    <div className="space-y-2">
                      {selectedInvoice.document_type === 'rs' ? (
                        // For receipts, only show email and phone
                        <>
                          <div>
                            <span className="text-sm font-medium text-gray-500">Email:</span>
                            <span className="ml-2 text-sm text-gray-900">{selectedInvoice.contact_email || 'N/A'}</span>
                          </div>
                          <div>
                            <span className="text-sm font-medium text-gray-500">Phone:</span>
                            <span className="ml-2 text-sm text-gray-900">{selectedInvoice.contact_telephone || 'N/A'}</span>
                          </div>
                        </>
                      ) : (
                        // For invoices, show all contact information
                        <>
                          <div>
                            <span className="text-sm font-medium text-gray-500">Name:</span>
                            <span className="ml-2 text-sm text-gray-900">{selectedInvoice.contact_profession || 'N/A'}</span>
                          </div>
                          <div>
                            <span className="text-sm font-medium text-gray-500">VAT Number:</span>
                            <span className="ml-2 text-sm text-gray-900">{selectedInvoice.contact_vat || 'N/A'}</span>
                          </div>
                          <div>
                            <span className="text-sm font-medium text-gray-500">Tax Office:</span>
                            <span className="ml-2 text-sm text-gray-900">{selectedInvoice.contact_tax_office || 'N/A'}</span>
                          </div>
                          <div>
                            <span className="text-sm font-medium text-gray-500">Email:</span>
                            <span className="ml-2 text-sm text-gray-900">{selectedInvoice.contact_email || 'N/A'}</span>
                          </div>
                          <div>
                            <span className="text-sm font-medium text-gray-500">Phone:</span>
                            <span className="ml-2 text-sm text-gray-900">{selectedInvoice.contact_telephone || 'N/A'}</span>
                          </div>
                          <div>
                            <span className="text-sm font-medium text-gray-500">Address:</span>
                            <span className="ml-2 text-sm text-gray-900">
                              {selectedInvoice.contact_address || 'N/A'}, {selectedInvoice.contact_city || ''} {selectedInvoice.contact_zip_code || ''}, {selectedInvoice.contact_country || ''}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Financial Info */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center mb-4">
                    <FaMoneyBillWave className="mr-2 text-yellow-500" />
                    Financial Information
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm font-medium text-gray-500">Net Amount</p>
                      <p className="text-lg font-bold text-gray-900">{formatCurrency(selectedInvoice.net_amount)}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm font-medium text-gray-500">VAT Amount</p>
                      <p className="text-lg font-bold text-gray-900">{formatCurrency(selectedInvoice.vat_amount)}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm font-medium text-gray-500">Total Amount</p>
                      <p className="text-lg font-bold text-gray-900">{formatCurrency(selectedInvoice.total_amount)}</p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <p className="text-sm font-medium text-green-600">Amount Paid</p>
                      <p className="text-lg font-bold text-green-700">{formatCurrency(selectedInvoice.amount_paid)}</p>
                    </div>
                    <div className="bg-yellow-50 p-4 rounded-lg">
                      <p className="text-sm font-medium text-yellow-600">Amount Pending</p>
                      <p className="text-lg font-bold text-yellow-700">{formatCurrency(selectedInvoice.amount_pending)}</p>
                    </div>
                  </div>
                </div>

                {/* Invoice Items */}
                {selectedInvoice.items && selectedInvoice.items.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Invoice Items</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full border border-gray-200 rounded-lg">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Unit Price</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">VAT</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {selectedInvoice.items.map((item, index) => (
                            <tr key={index}>
                              <td className="px-4 py-2 text-sm text-gray-900">{item.description}</td>
                              <td className="px-4 py-2 text-sm text-gray-900">{item.quantity}</td>
                              <td className="px-4 py-2 text-sm text-gray-900">{formatCurrency(item.unit_net_value)}</td>
                              <td className="px-4 py-2 text-sm text-gray-900">{formatCurrency(item.vat_amount)}</td>
                              <td className="px-4 py-2 text-sm text-gray-900 font-medium">{formatCurrency(item.item_total_amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex justify-end space-x-3">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => openInvoicePdf(selectedInvoice)}
                    className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    <FaDownload className="mr-2" />
                    Open PDF
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Empty State */}
      {!loading && invoices.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12"
        >
          <FaFileInvoice className="mx-auto text-6xl text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No invoices found</h3>
          <p className="text-gray-600 mb-4">No invoices match your current filters.</p>
          <button
            onClick={clearFilters}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Clear Filters
          </button>
        </motion.div>
      )}
    </div>
  );
};

export default OxygenInvoices;
